import { PartyRole, Remark } from "./db";
import { jsonSafeValueToDate } from "./typing";
import { DEFAULT_LANGUAGE_SETTING } from "./users";


export type Condition = {
  condition_id: string
  temp_condition_id?: string
  team_id: string
  filter_cnf: TemporalFilterDisjunction[]
  created_at: Date
  updated_at: Date
  active: boolean
  condition_name: string | null
  condition_group: string | null
  deployRequested?: boolean
}

export function jsonSafeToCondition(v: Record<string, any>): Condition {
  return {
    condition_id: v.condition_id,
    team_id: v.team_id,
    filter_cnf: jsonSafeToTemporalFilterDisjunctions(JSON.parse(v.filter_cnf_str)),
    created_at: jsonSafeValueToDate(v.created_at),
    updated_at: jsonSafeValueToDate(v.updated_at),
    active: v.active,
    condition_name: v.condition_name ? v.condition_name : null,
    condition_group: v.condition_group ? v.condition_group : null
  }
}

export function jsonSafeToConditionResult(v: Record<string, any>): Condition[] {
  return v.map((x: Record<string, any>) => jsonSafeToCondition(x))
}

export type Play = {
  play_id: string
  temp_play_id?: string
  team_id: string
  condition_id: string
  temp_condition_id?: string
  prompt_text: string
  created_at: Date
  updated_at: Date
  active: boolean
  weight: number
  play_name: string
  min_duration: number | null
  deployRequested?: boolean
}

export function jsonSafeToPlay(v: Record<string, any>): Play {
  return {
    play_id: v.play_id,
    team_id: v.team_id,
    condition_id: v.condition_id,
    prompt_text: v.prompt_text,
    created_at: jsonSafeValueToDate(v.created_at),
    updated_at: jsonSafeValueToDate(v.updated_at),
    active: v.active,
    weight: v.weight,
    play_name: v.play_name,
    min_duration: v.min_duration !== null ? v.min_duration : null
  }
}

export function jsonSafeToPlayResult(v: Record<string, any>): Play[] {
  return v.map((x: Record<string, any>) => jsonSafeToPlay(x))
}

export enum CnfFilterType {
  USER = 'USER',
  USER_ACCOUNT = 'USER_ACCOUNT',
  EXTERNAL_ACCOUNT = 'EXTERNAL_ACCOUNT',
  PROSPECT = 'PROSPECT',
  TIME = 'TIME',
  DISPLAY = 'DISPLAY',
  PHRASE = 'PHRASE',
  REMARK = 'REMARK',
  STAGE = 'STAGE',
  COUNTERPARTS = 'COUNTERPARTS',
  METADATA = 'METADATA',
  CUSTOM_METRIC = "CUSTOM_METRIC",
  SESSION_METRIC = "SESSION_METRIC",
  PROSPECT_INFO = 'PROSPECT_INFO'
}

export type UserFilterTerm = {
  user_id?: string
  user_group_id?: string
}

export function jsonSafeToUserFilterTerm(v: Record<string, any>): UserFilterTerm {
  return {
    user_id: v.user_id ? v.user_id : undefined,
    user_group_id: v.user_group_id ?? v.subteam_id, // filter_cnf_str from May 2024 and before might use subteam_id
  }
}

export function jsonSafeToUserFilterTerms(v: Record<string, any>): UserFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToUserFilterTerm(x))
}

export type UserAccountFilterTerm = {
  user_ids?: string[]
  user_group_ids?: string[]
  account_ids?: string[]
}

export function jsonSafeToUserAccountFilterTerm(v: Record<string, any>): UserAccountFilterTerm {
  return {
    user_ids: v.user_ids ? v.user_ids : undefined,
    user_group_ids: v.user_group_ids ? v.user_group_ids : undefined,
    account_ids: v.account_ids ? v.account_ids : undefined
  }
}

export function jsonSafeToUserAccountFilterTerms(v: Record<string, any>): UserAccountFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToUserAccountFilterTerm(x))
}

export type ExternalAccountFilterTerm = {
  external_account_id?: string
}

export function jsonSafeToExternalAccountFilterTerm(v: Record<string, any>): ExternalAccountFilterTerm {
  return {
    external_account_id: v.external_account_id ?? undefined,
  }
}

export function jsonSafeToExternalAccountFilterTerms(v: Record<string, any>): ExternalAccountFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToExternalAccountFilterTerm(x))
}

export type ProspectFilterTerm = {
  cadence_step?: string
  cadence?: string
  industry?: string
  title?: string
  seniority?: string
  phone_number?: string

  exact: boolean
  case_sensitive: boolean
}

export function jsonSafeToProspectFilterTerm(v: Record<string, any>): ProspectFilterTerm {
  return {
    cadence_step: v.cadence_step ? v.cadence_step : undefined,
    cadence: v.cadence ? v.cadence : undefined,
    industry: v.industry ? v.industry : undefined,
    title: v.title ? v.title : undefined,
    seniority: v.seniority ? v.seniority : undefined,
    phone_number: v.phone_number ? v.phone_number : undefined,
    exact: v.exact,
    case_sensitive: v.case_sensititive,
  }
}

export function jsonSafeToProspectFilterTerms(v: Record<string, any>): ProspectFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToProspectFilterTerm(x))
}

export type TimeFilterTerm = {
  start?: number
  end?: number
}

export enum TimeFilterType {
  BEFORE = 'Before',
  BETWEEN = 'Between',
  AFTER = 'After'
}

export function jsonSafeToTimeFilterTerm(v: Record<string, any>): TimeFilterTerm {
  return {
    start: v.start != null ? v.start : undefined,
    end: v.end != null ? v.end : undefined
  }
}

export function jsonSafeToTimeFilterTerms(v: Record<string, any>): TimeFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToTimeFilterTerm(x))
}


export type PhraseFilterTerm = {
  text: string
  party_role?: PartyRole
  stem: boolean
  language: DEFAULT_LANGUAGE_SETTING
}

export function jsonSafeToPhraseFilterTerm(v: Record<string, any>): PhraseFilterTerm {
  return {
    text: v.text,
    party_role: v.party_role ? v.party_role : undefined,
    stem: v.stem,
    language: v.language,
  }
}

export function jsonSafeToPhraseFilterTerms(v: Record<string, any>): PhraseFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToPhraseFilterTerm(x))
}

export type RemarkFilterTerm = {
  remark: Remark
  party_role?: PartyRole
}

export function jsonSafeToRemarkFilterTerm(v: Record<string, any>): RemarkFilterTerm {
  return {
    remark: v.remark,
    party_role: v.party_role ? v.party_role : undefined
  }
}

export function jsonSafeToRemarkFilterTerms(v: Record<string, any>): RemarkFilterTerm[] {
  return v.map((x: Record<string, any>) => jsonSafeToRemarkFilterTerm(x))
}

export enum LookbackRule {
  PRECEDED_BY = 'PRECEDED_BY',
  NOT_PRECEDED_BY = 'NOT_PRECEDED_BY'
}

export type TemporalFilterDisjunction = {
  filter_type: CnfFilterType
  user_filters?: UserFilterTerm[]
  prospect_filters?: ProspectFilterTerm[]
  time_filters?: TimeFilterTerm[]
  phrase_filters?: PhraseFilterTerm[]
  remark_filters?: RemarkFilterTerm[]
  lookback_rule?: LookbackRule
  lookback_horizon?: number

  prospectFilterPrimarilyFor?: 'CADENCE_STEP' | 'CADENCE' | 'INDUSTRY' | 'TITLE' | 'SENIORITY' | 'PHONE_NUMBER'
  userFilterPrimarilyFor?: 'USERS' | 'SUBTEAMS' | 'USERS_AND_SUBTEAMS'
  phraseFilterPrimarilyFor?: 'PROSPECT' | 'REP' | 'ANYONE'
  remarkFilterPrimarilyFor?: 'PROSPECT' | 'REP' | 'ANYONE'
  timeFilterPrimarilyFor?: TimeFilterType
}

export function jsonSafeToTemporalFilterDisjunction(v: Record<string, any>): TemporalFilterDisjunction {
  return {
    filter_type: v.filter_type,
    user_filters: v.user_filters ? jsonSafeToUserFilterTerms(v.user_filters) : undefined,
    prospect_filters: v.prospect_filters ? jsonSafeToProspectFilterTerms(v.prospect_filters) : undefined,
    time_filters: v.time_filters ? jsonSafeToTimeFilterTerms(v.time_filters) : undefined,
    phrase_filters: v.phrase_filters ? jsonSafeToPhraseFilterTerms(v.phrase_filters) : undefined,
    remark_filters: v.remark_filters ? jsonSafeToRemarkFilterTerms(v.remark_filters) : undefined,
    lookback_rule: v.lookback_rule ? v.lookback_rule : undefined,
    lookback_horizon: v.lookback_horizon != null ? v.lookback_horizon : undefined,
  }
}
export function TemporalFilterDisjunctionToCleanTemporalFilterDisjunction(temporalFilters: TemporalFilterDisjunction): TemporalFilterDisjunction {
  return {
    filter_type: temporalFilters.filter_type,
    user_filters: temporalFilters.user_filters,
    prospect_filters: temporalFilters.prospect_filters,
    time_filters: temporalFilters.time_filters,
    phrase_filters: temporalFilters.phrase_filters,
    remark_filters: temporalFilters.remark_filters,
    lookback_rule: temporalFilters.lookback_rule,
    lookback_horizon: temporalFilters.lookback_horizon
  }
}

export function cleanTemporalFilterDisjunctions(temporalFilters: TemporalFilterDisjunction[]): TemporalFilterDisjunction[] {
  return temporalFilters.map((v) => TemporalFilterDisjunctionToCleanTemporalFilterDisjunction(v))
}

export function jsonSafeToTemporalFilterDisjunctions(v: Record<string, any>): TemporalFilterDisjunction[] {
  return v.map((x: Record<string, any>) => jsonSafeToTemporalFilterDisjunction(x))
}

